import { Tirage } from "@/api/tirages/tirage.api";
import i18n, { SupportedLocale } from "@/i18n";
import { computeFieldTirageDimensions } from "@/lib/computeFields";
import { MoneyAmount } from "@/lib/shared-types";
import { parseJsonOrUndefined } from "@/lib/utils";
import { AdvancedSearchConfig } from "../forms/advanced-search/advanced-search-fields-def";
import { ColumnFormatted } from "./general";

export const tiragesColumns: ColumnFormatted[] = [
  {
    type: "string",
    key: "tirageDescriptionNumeroEnregistrement",
    visible: true,
  },
  { type: "image", key: "vignette", visible: true },
  { type: "string", key: "tirageDescriptionNumeroInventaire", visible: true },
  {
    type: "computed",
    key: "tirageDescriptionType",
    visible: true,
    formattedData(data) {
      if (Object.keys(data.tirageDescriptionType ?? {}).length === 0) return "";
      return (
        data.tirageDescriptionType?.[i18n.language as SupportedLocale] ?? ""
      );
    },
  },

  {
    type: "computed",
    key: "tirageDescriptionDimensionsWithMargins",
    formattedData: (data: Tirage) => {
      return computeFieldTirageDimensions(
        {
          unknownDimensions: false,
          ...(parseJsonOrUndefined(
            data.tirageDescriptionDimensionsWithMargins,
          ) ?? {}),
        },
        {
          language: i18n.language as SupportedLocale,
        },
      );
    },
    visible: true,
  },
  {
    type: "computed",
    key: "tirageDescriptionDimensionsWithoutMargins",
    formattedData: (data: Tirage) => {
      return computeFieldTirageDimensions(
        {
          unknownDimensions: false,
          ...(parseJsonOrUndefined(
            data.tirageDescriptionDimensionsWithoutMargins,
          ) ?? {}),
        },
        {
          language: i18n.language as SupportedLocale,
        },
      );
    },
    visible: true,
  },
  { type: "string", key: "tirageDescriptionDateCreation", visible: true },
  {
    type: "computed",
    key: "tirageDescriptionPrinter",
    visible: true,
    formattedData(data) {
      if (Object.keys(data.tirageDescriptionPrinter ?? {}).length === 0)
        return "";
      return (
        data.tirageDescriptionPrinter?.[i18n.language as SupportedLocale] ?? ""
      );
    },
  },
  { type: "richtext", key: "tirageDescriptionTechnicalNotes" },
  { type: "richtext", key: "tirageDescriptionConditionNotes" },
  { type: "richtext", key: "tirageDescriptionNotes" },
  {
    type: "computed",
    key: "tirageDescriptionTechnique",
    formattedData: (data: Tirage) => {
      if (Object.keys(data.tirageDescriptionTechnique ?? {}).length === 0)
        return "";
      return (
        data.tirageDescriptionTechnique?.[i18n.language as SupportedLocale] ??
        ""
      );
    },
  },
  {
    type: "computed",
    key: "tirageDescriptionSupport",
    formattedData: (data: Tirage) => {
      if (Object.keys(data.tirageDescriptionSupport ?? {}).length === 0)
        return "";
      return (
        data.tirageDescriptionSupport?.[i18n.language as SupportedLocale] ?? ""
      );
    },
  },
  {
    type: "computed",
    key: "tirageDescriptionEditionNumber",
    formattedData: (data: Tirage) => {
      if (Object.keys(data.tirageDescriptionEditionNumber ?? {}).length === 0)
        return "";
      return (
        data.tirageDescriptionEditionNumber?.[
          i18n.language as SupportedLocale
        ] ?? ""
      );
    },
  },
  {
    type: "computed",
    key: "tirageDescriptionSignatureInscription",
    formattedData: (data: Tirage) => {
      if (
        Object.keys(data.tirageDescriptionSignatureInscription ?? {}).length ===
        0
      )
        return "";
      return (
        data.tirageDescriptionSignatureInscription?.[
          i18n.language as SupportedLocale
        ] ?? ""
      );
    },
  },
  {
    type: "computed",
    key: "tirageDescriptionSignatureInscriptionDos",
    formattedData: (data: Tirage) => {
      if (
        Object.keys(data.tirageDescriptionSignatureInscriptionDos ?? {})
          .length === 0
      )
        return "";
      return (
        data.tirageDescriptionSignatureInscriptionDos?.[
          i18n.language as SupportedLocale
        ] ?? ""
      );
    },
  },
  { type: "string", key: "tirageEstimationDescription" },
  {
    type: "computed",
    key: "tirageEstimationAmount",
    formattedData: (data: Tirage) => {
      const parsedData = parseJsonOrUndefined(
        data.tirageEstimationAmount,
      ) as MoneyAmount;

      if (Object.keys(parsedData ?? {}).length === 0) return "";
      return `${parsedData.amount} ${parsedData.currency}`;
    },
  },
  { type: "status", key: "tiragePublicationStatus" },
  { type: "string", key: "tirageSaleDescription" },
  {
    type: "computed",
    key: "tirageSaleAmount",
    formattedData: (data: Tirage) => {
      const parsedData = parseJsonOrUndefined(
        data.tirageSaleAmount,
      ) as MoneyAmount;

      if (Object.keys(parsedData ?? {}).length === 0) return "";
      return `${parsedData.amount} ${parsedData.currency}`;
    },
  },
  { type: "string", key: "tirageAcquisitionDescription" },
  {
    type: "computed",
    key: "tirageAcquisitionAmount",
    formattedData: (data: Tirage) => {
      const parsedData = parseJsonOrUndefined(
        data.tirageAcquisitionAmount,
      ) as MoneyAmount;

      if (Object.keys(parsedData ?? {}).length === 0) return "";
      return `${parsedData.amount} ${parsedData.currency}`;
    },
  },
  { type: "string", key: "tirageStorageLocation1" },
  { type: "string", key: "tirageStorageLocation2" },
  { type: "string", key: "tirageStorageLocation3" },
];

export const tiragesColumnsVisibility = tiragesColumns.reduce(
  (acc, column) => ({
    ...acc,
    [column.key]: column.visible || false,
  }),
  {},
);

export const tiragesAdvancedSearchConfig: AdvancedSearchConfig = [
  {
    type: "group",
    label: "tiragesDescription",
    fields: [
      { type: "string", key: "tirageDescriptionNumeroEnregistrement" },
      { type: "string", key: "tirageDescriptionNumeroInventaire" },
      { type: "multilingual", key: "tirageDescriptionType" },
      { type: "string", key: "tirageDescriptionDateCreation" },
      { type: "multilingual", key: "tirageDescriptionPrinter" },
      { type: "string", key: "tirageDescriptionTechnicalNotes" },
      { type: "string", key: "tirageDescriptionConditionNotes" },
      { type: "string", key: "tirageDescriptionNotes" },
      { type: "multilingual", key: "tirageDescriptionTechnique" },
      { type: "multilingual", key: "tirageDescriptionSupport" },
      { type: "multilingual", key: "tirageDescriptionEditionNumber" },
      { type: "multilingual", key: "tirageDescriptionSignatureInscription" },
      { type: "string", key: "tirageEstimationDescription" },
      { type: "currency", key: "tirageEstimationAmount" },
      { type: "string", key: "tirageSaleDescription" },
      { type: "currency", key: "tirageSaleAmount" },
      { type: "string", key: "tirageAcquisitionDescription" },
      { type: "currency", key: "tirageAcquisitionAmount" },
      { type: "string", key: "tirageStorageLocation1" },
      { type: "string", key: "tirageStorageLocation2" },
      { type: "string", key: "tirageStorageLocation3" },
    ],
  },
];
